'use client';

import { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { PUBLIC_INSTAGRAM_DM_URL } from '@/config/publicContact';
import { useLandingHeaderScrolled } from '@/lib/hooks/useLandingHeaderScrolled';
import { trackEvent } from '@/lib/analytics/firebaseAnalytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { scrollToLandingSection } from '@/utils/scrollToLandingSection';
import styles from './LandingStickyBar.module.css';

const BAR_HIDE_DURATION_MS = 320;

/**
 * Sticky bottom bar on the landing page.
 * Appears at the same scroll point as the header solid background, hidden while pricing is in view.
 */
export default function LandingStickyBar() {
  const pastHeaderThreshold = useLandingHeaderScrolled();
  const [pricingInView, setPricingInView] = useState(false);

  useEffect(() => {
    const pricing = document.getElementById('pricing');
    if (!pricing) return;

    const pricingObserver = new IntersectionObserver(
      ([entry]) => setPricingInView(entry.isIntersecting),
      { threshold: 0 }
    );

    pricingObserver.observe(pricing);

    return () => {
      pricingObserver.disconnect();
    };
  }, []);

  const visible = pastHeaderThreshold && !pricingInView;

  // Reserve space at the bottom so the fixed bar does not cover footer content.
  // Delay removal until the slide-out animation finishes.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (visible) {
      document.documentElement.style.setProperty(
        '--landing-sticky-bar-offset',
        '80px'
      );
      return;
    }

    const timer = window.setTimeout(() => {
      document.documentElement.style.setProperty('--landing-sticky-bar-offset', '0px');
    }, BAR_HIDE_DURATION_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [visible]);

  const handleJoinClick = () => {
    trackEvent(AnalyticsEvents.CLICK_JOIN_BUTTON, {
      source: 'landing_sticky_bar',
      cta_type: 'scroll_to_pricing',
    });
    scrollToLandingSection('pricing');
  };

  const handleInstagramClick = () => {
    trackEvent(AnalyticsEvents.CLICK_INSTAGRAM_LINK, {
      source: 'landing_sticky_bar',
      link_type: 'dm',
    });
  };

  return (
    <div
      className={`${styles.bar} ${visible ? styles.visible : ''}`}
      role="region"
      aria-label="Join the community"
      aria-hidden={!visible}
      inert={visible ? undefined : true}
    >
      <div className={styles.inner}>
        <button type="button" className={styles.joinBtn} onClick={handleJoinClick}>
          Join the Community
        </button>
        <a
          href={PUBLIC_INSTAGRAM_DM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.instagramBtn}
          aria-label="Message Lior on Instagram"
          onClick={handleInstagramClick}
        >
          <Send size={20} strokeWidth={2.25} aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}
